import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { StatusTransitionModal } from '../../components/StatusTransitionModal.js';
import { Button } from '../../components/ui/Button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/ui/Card.js';
import { FwaApiError } from '../../lib/api.js';
import { useAdelanto, useUpdateAdelanto } from '../../lib/queries/adelantos.js';

const STATUS_BADGE: Record<string, { label: string; tone: string }> = {
  borrador: { label: 'Borrador', tone: 'bg-amber-100 text-amber-800' },
  otorgado: { label: 'Otorgado', tone: 'bg-green-100 text-green-800' },
  revocado: { label: 'Revocado', tone: 'bg-red-100 text-red-800' },
};

export function AdelantosView() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error, refetch } = useAdelanto(id);
  const update = useUpdateAdelanto();
  const [modalMode, setModalMode] = useState<'otorgar' | 'revocar' | null>(null);
  const [serverError, setServerError] = useState<string>('');

  if (isLoading) return <div className="text-center py-8 text-gray-600">Cargando…</div>;
  if (error)
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700" role="alert">
        Error: {error.message}
      </div>
    );
  if (!data || !id) return null;

  const adelanto = data.data;
  const etag = data.etag ?? adelanto.version_id;
  const status = STATUS_BADGE[adelanto.estatus] ?? STATUS_BADGE.borrador;

  const handleTransition = (payload: Record<string, unknown>) => {
    setServerError('');
    update.mutate(
      {
        id,
        payload: payload as Parameters<typeof update.mutate>[0]['payload'],
        etag,
      },
      {
        onSuccess: () => {
          setModalMode(null);
          void refetch();
        },
        onError: (err) => {
          if (err instanceof FwaApiError) {
            setServerError(err.problem.detail ?? err.problem.title);
          } else {
            setServerError(err.message);
          }
        },
      },
    );
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">{adelanto.nombre_adelanto}</h1>
        <span
          className={`inline-block px-3 py-1 rounded text-sm font-semibold ${status?.tone ?? ''}`}
        >
          {status?.label ?? adelanto.estatus}
        </span>
      </div>

      {serverError && (
        <div
          className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700"
          role="alert"
        >
          {serverError}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Datos del adelanto</CardTitle>
        </CardHeader>
        <CardBody>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <Field label="Tipo" value={adelanto.tipo_adelanto} />
            <Field label="Niña entity_id" value={adelanto.nina_entity_id} mono />
            <Field label="Etapa anterior" value={adelanto.etapa_anterior ?? '—'} />
            <Field label="Etapa nueva" value={adelanto.etapa_nueva ?? '—'} />
            <Field label="Fecha de otorgamiento" value={adelanto.fecha_otorgamiento ?? '—'} />
            <Field label="Fecha de revocación" value={adelanto.fecha_revocacion ?? '—'} />
            <Field label="Comentario" value={adelanto.comentario ?? '—'} fullWidth />
            <Field
              label="Motivo de revocación"
              value={adelanto.motivo_revocacion ?? '—'}
              fullWidth
            />
            <Field label="Creado por" value={adelanto.created_by} />
            <Field label="Creado en" value={adelanto.created_at} />
          </dl>
        </CardBody>
      </Card>

      <div className="flex gap-3">
        {adelanto.estatus === 'borrador' && (
          <Button variant="primary" onClick={() => setModalMode('otorgar')}>
            Otorgar
          </Button>
        )}
        {adelanto.estatus === 'otorgado' && (
          <Button variant="primary" onClick={() => setModalMode('revocar')}>
            Revocar
          </Button>
        )}
        {adelanto.estatus !== 'revocado' && (
          <Link to={`/adelantos/${id}/edit`}>
            <Button variant="ghost">Editar</Button>
          </Link>
        )}
        <Link to="/adelantos">
          <Button variant="ghost">← Regresar al listado</Button>
        </Link>
      </div>

      {modalMode === 'otorgar' && (
        <StatusTransitionModal
          mode="otorgar"
          onConfirm={(payload) => handleTransition(payload)}
          onCancel={() => setModalMode(null)}
          isSubmitting={update.isPending}
        />
      )}
      {modalMode === 'revocar' && (
        <StatusTransitionModal
          mode="revocar"
          onConfirm={(payload) => handleTransition(payload)}
          onCancel={() => setModalMode(null)}
          isSubmitting={update.isPending}
        />
      )}
    </div>
  );
}

function Field({
  label,
  value,
  mono,
  fullWidth,
}: {
  label: string;
  value: string;
  mono?: boolean;
  fullWidth?: boolean;
}) {
  return (
    <div className={fullWidth ? 'sm:col-span-2' : ''}>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className={`text-gray-900 ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
    </div>
  );
}
